import { useCallback } from 'react'
import { useSubmitEnrollmentStep } from '@/api/enrollment-api'
import type { ApplyHydrationFn, FlowNavigation } from '../types'
import { resolveNextStepAdvance } from './stepSubmission/resolveNextStepAdvance'
import { validateStepContinuation, type ValidateStepContinuationOptions } from './stepSubmission/validateStepContinuation'
import { useStepSubmitAction } from './stepSubmission/useStepSubmitAction'
import { SELF_ENROLLMENT_STEPS } from '../utils/steps'

type UseStepSubmissionArgs = {
  currentStep: number
  totalSteps: number
  enrollmentSessionToken: string | null
  navigation: Pick<FlowNavigation, 'navigate' | 'goToStep'> & {
    scrollToTop: () => void
    suppressNextSessionStepSync: () => void
  }
  applyHydration: ApplyHydrationFn
  setStepSubmissionError: (value: string | null) => void
  setStepValidationError: (value: string | null) => void
  setIsCheckingStepAccess: (value: boolean) => void
  submitEnrollmentStepMutation: ReturnType<typeof useSubmitEnrollmentStep>
}

export function useStepSubmission({
  currentStep,
  totalSteps,
  enrollmentSessionToken,
  navigation,
  applyHydration,
  setStepSubmissionError,
  setStepValidationError,
  setIsCheckingStepAccess,
  submitEnrollmentStepMutation
}: UseStepSubmissionArgs) {
  const { navigate, goToStep, scrollToTop, suppressNextSessionStepSync } = navigation

  const validateStepBeforeContinue = useCallback(
    (
      step: number,
      options?: ValidateStepContinuationOptions
    ) =>
      validateStepContinuation({
        step,
        totalSteps,
        enrollmentSessionToken,
        navigate,
        setStepSubmissionError,
        setStepValidationError,
        setIsCheckingStepAccess,
        options
      }),
    [
      enrollmentSessionToken,
      navigate,
      setIsCheckingStepAccess,
      setStepSubmissionError,
      setStepValidationError,
      totalSteps
    ]
  )

  const handleSubmitStep = useStepSubmitAction({
    applyHydration,
    enrollmentSessionToken,
    goToStep,
    scrollToTop,
    setIsCheckingStepAccess,
    setStepSubmissionError,
    submitEnrollmentStepMutation,
    suppressNextSessionStepSync,
    validateStepBeforeContinue
  })

  const handleEnglishAssessmentPassed = useCallback(async () => {
    setStepSubmissionError(null)
    setStepValidationError(null)

    await handleSubmitStep(SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY, {}, { onSuccess: () => {} })
  }, [handleSubmitStep, setStepSubmissionError, setStepValidationError])

  const handleNext = useCallback(
    async (step?: number) => {
      const stepToValidate = typeof step === 'number' ? step : currentStep
      const canContinue = validateStepBeforeContinue(stepToValidate)

      if (!canContinue) {
        return
      }

      resolveNextStepAdvance({
        currentStep,
        totalSteps,
        step,
        navigate,
        goToStep,
        suppressNextSessionStepSync
      })
    },
    [
      currentStep,
      goToStep,
      navigate,
      suppressNextSessionStepSync,
      totalSteps,
      validateStepBeforeContinue
    ]
  )

  return {
    validateStepBeforeContinue,
    handleSubmitStep,
    handleEnglishAssessmentPassed,
    handleNext
  }
}
